/**
 * // Class Obj: 要素の高さをアニメーションしながら開閉（jQueryのslideToggle()メソッド相当）
 * 単体の要素に対する「開く」「閉じる」
 */
export class SlideController
{
  /**
   * デフォルトのアニメーション時間（ミリ秒）
   * 各メソッドで上書き指定がなければこれを使う
   */
  private readonly mDefaultDuration: number;

  /**
   * transitionend ハンドラとフォールバックタイマーを要素ごとに管理
   */
  private readonly mTransitionHandlers: WeakMap<HTMLElement, (ev: TransitionEvent) => void> = new WeakMap();
  private readonly mFallbackTimeouts: WeakMap<HTMLElement, number> = new WeakMap();

  /**
   * // Constructor: アニメーション時間のデフォルト値を設定
   *
   * @param default_duration アニメーション時間（ミリ秒）デフォルト500ms
   */
  constructor(default_duration: number = 500)
  {
    this.mDefaultDuration = default_duration;
  }

  /**
   * // Method: 実際に使用するdurationを取得（メソッド引数優先）
   */
  private getDuration(duration?: number): number
  {
    if (typeof duration === 'number' && duration >= 0)
    {
      return duration;
    }
    return this.mDefaultDuration;
  }

  /**
   * // Method: 要素の元々の display 値を取得（初回は計算してdata属性に保存）
   *
   * @param {HTMLElement} element 高さアニメーションを適用する要素（閉じている要素→.contentなど）
   *
   * @return {string} 元々のdisplay値（none の場合は block をフォールバック）
   */
  private getOriginalDisplay(element: HTMLElement): string
  {
    const saved: string | undefined = element.dataset.originalDisplay;
    if (saved) return saved;

    const computed: CSSStyleDeclaration = window.getComputedStyle(element);
    const display: string = computed.display === 'none' ? 'block' : computed.display;

    element.dataset.originalDisplay = display;

    return display;
  }

  /**
   * // Method: 現在の表示状態に応じて slideDown() / slideUp() を切り替える
   *
   * @param element 開閉したい要素
   * @param duration アニメーション時間（ミリ秒）※省略時はデフォルト
   */
  public slideToggle(element: HTMLElement, duration?: number): void
  {
    const use_duration: number = this.getDuration(duration);

    // 途中で連打された場合は、いったん現在のトランジションを確定させる
    if (this.isAnimating(element))
    {
      this.cancelTransition(element);
    }

    const is_hidden: boolean = this.isHidden(element);

    if (is_hidden)
    {
      this.slideDown(element, use_duration);
    }
    else
    {
      this.slideUp(element, use_duration);
    }
  }

  /**
   * // Method: 要素をスライドダウン（非表示 → 表示）する
   *
   * @param element 開きたい要素
   * @param duration アニメーション時間（ミリ秒）※省略時はデフォルト
   */
  public slideDown(element: HTMLElement, duration?: number): void
  {
    const use_duration: number = this.getDuration(duration);

    if (this.isAnimating(element))
    {
      this.cancelTransition(element);
    }

    this.setAnimating(element, true);

    const original_display: string = this.getOriginalDisplay(element);
    const was_hidden: boolean = this.isHidden(element);

    // 必ず表示状態にする
    element.style.display = original_display;
    element.style.overflow = 'hidden';

    // 目標高さ（中身に合わせた高さ）
    element.style.height = 'auto';
    const target_height: number = element.scrollHeight;

    // 開始高さ
    let start_height: number;

    if (was_hidden)
    {
      // 完全に閉じていた場合は 0 からスタート
      start_height = 0;
    }
    else
    {
      // 途中キャンセルなどの場合は「今見えている高さ」からスタート
      const computed: CSSStyleDeclaration = window.getComputedStyle(element);
      const current_height: number = parseFloat(computed.height);
      start_height = Number.isNaN(current_height) ? 0 : current_height;
    }

    // 開始高さをセット
    element.style.height = `${start_height}px`;
    element.style.willChange = 'height';

    // 初期値を確定させるための強制リフロー
    element.getBoundingClientRect();

    // トランジション設定 → 目標高さまでアニメーション
    element.style.transition = `height ${use_duration}ms ease-in-out`;
    element.style.height = `${target_height}px`;

    const cleanup = () =>
    {
      element.style.transition = '';
      element.style.height = '';
      element.style.overflow = '';
      element.style.willChange = '';
      this.setAnimating(element, false);

      element.dispatchEvent(
        new CustomEvent(
          'slidetoggle:end',
          { detail: { state: 'open' as const } }
        )
      );
    };

    this.registerHeightTransition(element, cleanup, use_duration);
  }

  /**
   * // Method: 要素をスライドアップ（表示 → 非表示）
   *
   * @param element 閉じたい要素
   * @param duration アニメーション時間（ミリ秒）※省略時はデフォルト
   */
  public slideUp(element: HTMLElement, duration?: number): void
  {
    const use_duration: number = this.getDuration(duration);

    if (this.isAnimating(element))
    {
      this.cancelTransition(element);
    }

    this.setAnimating(element, true);

    let computed: CSSStyleDeclaration = window.getComputedStyle(element);

    // 万が一 display:none なら、計測のために一旦表示
    if (computed.display === 'none')
    {
      element.style.display = this.getOriginalDisplay(element);
      computed = window.getComputedStyle(element);
    }

    // 現在の高さ（開始高さ）
    let start_height: number = parseFloat(computed.height);
    if (Number.isNaN(start_height) || start_height === 0)
    {
      // auto 等で 0 になっている場合は scrollHeight で補正
      start_height = element.scrollHeight;
    }

    element.style.height = `${start_height}px`;
    element.style.overflow = 'hidden';
    element.style.willChange = 'height';

    // 初期値を確定させるための強制リフロー
    element.getBoundingClientRect();

    // 0px に向けてトランジション
    element.style.transition = `height ${use_duration}ms ease-in-out`;
    element.style.height = '0px';

    const cleanup = () =>
    {
      element.style.transition = '';
      element.style.display = 'none';
      element.style.height = '';
      element.style.overflow = '';
      element.style.willChange = '';
      this.setAnimating(element, false);

      element.dispatchEvent(
        new CustomEvent(
          'slidetoggle:end',
          { detail: { state: 'closed' as const } }
        )
      );
    };

    this.registerHeightTransition(element, cleanup, use_duration);
  }

  /**
   * // Method: 要素が現在アニメーション中かどうかを判定
   */
  private isAnimating(element: HTMLElement): boolean
  {
    return element.dataset.animating === 'true';
  }

  /**
   * // Method: 要素が「非表示状態(display: none)」かどうかを判定
   */
  public isHidden(element: HTMLElement): boolean
  {
    const style: CSSStyleDeclaration = window.getComputedStyle(element);
    return style.display === 'none';
  }

  /**
   * // Method: アニメーション中フラグをセットし、状態を管理
   */
  private setAnimating(element: HTMLElement, state: boolean): void
  {
    element.dataset.animating = state ? 'true' : 'false';
    element.style.pointerEvents = state ? 'none' : '';
  }

  /**
   * // Method: height プロパティの transitionend / フォールバックを登録
   *
   * @param duration_ms このアニメーションで実際に使ったduration（ミリ秒）
   */
  private registerHeightTransition(
    element: HTMLElement,
    cleanup: () => void,
    duration_ms: number
  ): void
  {
    const onEnd = (ev: TransitionEvent) =>
    {
      if (ev.propertyName !== 'height') return;

      element.removeEventListener('transitionend', onEnd);
      this.mTransitionHandlers.delete(element);

      const timeout_id = this.mFallbackTimeouts.get(element);
      if (timeout_id !== undefined)
      {
        window.clearTimeout(timeout_id);
        this.mFallbackTimeouts.delete(element);
      }

      cleanup();
    };

    const prev_handler = this.mTransitionHandlers.get(element);
    if (prev_handler)
    {
      element.removeEventListener('transitionend', prev_handler);
    }
    const prev_timeout = this.mFallbackTimeouts.get(element);
    if (prev_timeout !== undefined)
    {
      window.clearTimeout(prev_timeout);
    }

    this.mTransitionHandlers.set(element, onEnd);
    element.addEventListener('transitionend', onEnd);

    const timeout_id: number = window.setTimeout(() =>
    {
      if (this.isAnimating(element))
      {
        element.removeEventListener('transitionend', onEnd);
        this.mTransitionHandlers.delete(element);
        this.mFallbackTimeouts.delete(element);
        cleanup();
      }
    }, duration_ms + 50);

    this.mFallbackTimeouts.set(element, timeout_id);
  }

  /**
   * // Method: 現在進行中の transition をキャンセルし、
   *            「いまの高さ」を起点に次のアニメーションへ繋ぎ直せる状態にする
   */
  private cancelTransition(element: HTMLElement): void
  {
    const handler = this.mTransitionHandlers.get(element);
    if (handler)
    {
      element.removeEventListener('transitionend', handler);
      this.mTransitionHandlers.delete(element);
    }

    const timeout_id = this.mFallbackTimeouts.get(element);
    if (timeout_id !== undefined)
    {
      window.clearTimeout(timeout_id);
      this.mFallbackTimeouts.delete(element);
    }

    const computed: CSSStyleDeclaration = window.getComputedStyle(element);
    const current_height: string = computed.height;

    element.style.transition = 'none';
    element.style.height = current_height;
    element.style.overflow = 'hidden';
    element.style.willChange = 'height';

    element.getBoundingClientRect();

    this.setAnimating(element, false);
  }
}

